/**
 * User Cost Table Component
 * Displays user cost distribution and top users
 */

import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { CostTier, CostConfig, UserChatStats } from '@/lib/api/reports';
import clsx from 'clsx';

interface UserCostTableProps {
  costTiers: CostTier[];
  config: CostConfig;
  className?: string;
}

export function UserCostTable({ costTiers, config, className }: UserCostTableProps) {
  const formatCurrency = (value: number) => `¥${value.toFixed(2)}`;

  return (
    <div className={clsx('space-y-6', className)}>
      {/* Cost Tier Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                成本区间
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                用户数
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                占比
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                平均成本
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                ¥{config.member_price}覆盖
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                单用户盈亏
              </th>
            </tr>
          </thead>
          <tbody>
            {costTiers.map((tier) => (
              <tr
                key={tier.label}
                className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
              >
                <td className="px-4 py-3 text-sm text-gray-900">{tier.label}</td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {tier.user_count}人
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {tier.percentage.toFixed(1)}%
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {formatCurrency(tier.avg_cost)}
                </td>
                <td className="px-4 py-3 text-sm">
                  {tier.covered_by_membership ? (
                    <span className="text-green-600 font-medium">✓ 是</span>
                  ) : (
                    <span className="text-red-600 font-medium">✗ 否</span>
                  )}
                </td>
                <td
                  className={clsx(
                    'px-4 py-3 text-sm font-medium',
                    tier.profit_per_user >= 0 ? 'text-green-600' : 'text-red-600'
                  )}
                >
                  {formatCurrency(tier.profit_per_user)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface CostDistributionChartProps {
  costTiers: CostTier[];
  className?: string;
}

export function CostDistributionChart({ costTiers, className }: CostDistributionChartProps) {
  const data = useMemo(() => {
    return costTiers.map((tier) => ({
      label: tier.label,
      count: tier.user_count,
      covered: tier.covered_by_membership,
    }));
  }, [costTiers]);

  return (
    <div className={className}>
      <h4 className="text-sm font-medium text-gray-600 mb-3">用户成本分层</h4>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="label" stroke="#888" fontSize={12} />
            <YAxis stroke="#888" fontSize={12} />
            <Tooltip />
            <Bar dataKey="count" name="用户数">
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.covered ? 'rgba(16, 185, 129, 0.6)' : 'rgba(239, 68, 68, 0.6)'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

interface TopUsersChartProps {
  users: UserChatStats[];
  memberPrice: number;
  limit?: number;
  className?: string;
}

export function TopUsersChart({ users, memberPrice, limit = 10, className }: TopUsersChartProps) {
  const data = useMemo(() => {
    return [...users]
      .sort((a, b) => b.total_cost_cny - a.total_cost_cny)
      .slice(0, limit)
      .map((u) => ({
        name: u.name || `User_${u.user_id.slice(0, 8)}`,
        cost: u.total_cost_cny,
        overBudget: u.total_cost_cny > memberPrice,
      }));
  }, [users, limit, memberPrice]);

  return (
    <div className={className}>
      <h4 className="text-sm font-medium text-gray-600 mb-3">Top {limit} 高成本用户</h4>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              type="number"
              stroke="#888"
              fontSize={12}
              tickFormatter={(v) => `¥${v}`}
            />
            <YAxis
              type="category"
              dataKey="name"
              stroke="#888"
              fontSize={11}
              width={80}
              tick={{ fill: '#374151' }}
            />
            <Tooltip
              formatter={(value) => {
                const numValue = typeof value === 'number' ? value : 0;
                return [`¥${numValue.toFixed(2)}`, '总成本'];
              }}
            />
            <Bar dataKey="cost" name="总成本">
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.overBudget ? 'rgba(239, 68, 68, 0.6)' : 'rgba(16, 185, 129, 0.6)'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

interface LifecyclePnlTableProps {
  users: UserChatStats[];
  limit?: number;
  className?: string;
}

export function LifecyclePnlTable({ users, limit = 20, className }: LifecyclePnlTableProps) {
  const sorted = useMemo(
    () =>
      [...users]
        .sort((a, b) => b.lifetime_cost_cny - a.lifetime_cost_cny)
        .slice(0, limit),
    [users, limit]
  );

  const statusLabel = (status: UserChatStats['payback_status']) => {
    switch (status) {
      case 'covered':
        return <span className="text-green-600 font-medium">已回本</span>;
      case 'uncovered':
        return <span className="text-red-600 font-medium">未回本</span>;
      default:
        return <span className="text-gray-400">免费用户</span>;
    }
  };

  return (
    <div className={className}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">用户</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                LTV 净收入
                <br />
                <span className="text-xs font-normal text-gray-400">全历史,不随时间筛选</span>
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                LTC 成本
                <br />
                <span className="text-xs font-normal text-gray-400">全历史,不随时间筛选</span>
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">回本状态</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((user) => (
              <tr key={user.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">
                  {user.name || `User_${user.user_id.slice(0, 8)}`}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  ¥{user.lifetime_net_revenue_cny.toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  ¥{user.lifetime_cost_cny.toFixed(4)}
                </td>
                <td className="px-4 py-3 text-sm">{statusLabel(user.payback_status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
